import { inject, Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { ANALYTICS_CONFIG, AnalyticsConfig } from '../tokens/analytics.tokens';
import { AnalyticsEventProps } from '../analytics/analytics-events';

export interface AnalyticsUserProperties {
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  companyName?: string;
  [key: string]: unknown;
}

/**
 * Thin wrapper around posthog-js so the rest of the app can call
 * a single, typed API. All methods are safe to call when analytics
 * is disabled (no posthog key configured) — they simply no-op.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly config = inject(ANALYTICS_CONFIG);
  private isInitialized = false;

  /** Whether analytics is enabled (posthog key is configured). */
  get isEnabled(): boolean {
    return !!this.config?.posthogKey;
  }

  /** Initialize PostHog. Call once at app startup. */
  init(): void {
    if (!this.isEnabled || this.isInitialized) return;

    try {
      posthog.init(this.config.posthogKey, {
        api_host: this.config.posthogHost,
        autocapture: false,
        rageclick: false,
        capture_dead_clicks: false,
        capture_pageview: false, // handled manually via the router
        capture_pageleave: true,
        persistence: 'localStorage+cookie',
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: {
            password: true
          }
        },
        loaded: ph => {
          if (this.config.environment) {
            ph.register({ environment: this.config.environment });
          }
        }
      });
      this.isInitialized = true;
    } catch (err) {
      console.error('[AnalyticsService] Failed to initialize PostHog', err);
    }
  }

  /** Associate the current session with a logged-in user. */
  identify(userId: string, properties?: AnalyticsUserProperties): void {
    if (!this.isReady()) return;
    try {
      posthog.identify(userId, properties);
    } catch (err) {
      console.error('[AnalyticsService] identify failed', err);
    }
  }

  /** Clear the current user's identity. Call on logout. */
  reset(): void {
    if (!this.isReady()) return;
    try {
      posthog.reset();
    } catch (err) {
      console.error('[AnalyticsService] reset failed', err);
    }
  }

  /** Manually capture a page view (we have `capture_pageview: false`). */
  capturePageView(url: string, title?: string): void {
    if (!this.isReady()) return;
    try {
      posthog.capture('$pageview', {
        $current_url: url,
        ...(title ? { page_title: title } : {})
      });
    } catch (err) {
      console.error('[AnalyticsService] capturePageView failed', err);
    }
  }

  /**
   * Capture a typed custom event.
   * Business events require LotOrDeal props (lot_id and/or deal_id) — missing ids are a compile error.
   * Extra keys (e.g. full API request/response payloads) are allowed.
   */
  capture<E extends keyof AnalyticsEventProps>(
    eventName: E,
    properties: AnalyticsEventProps[E] & Record<string, any>
  ): void {
    if (!this.isReady()) return;
    try {
      posthog.capture(eventName, properties as Record<string, unknown>);
    } catch (err) {
      console.error('[AnalyticsService] capture failed', err);
    }
  }

  /** Capture an API error from the HTTP interceptor. */
  captureApiError(params: {
    status: number;
    url: string;
    method?: string;
    message?: string;
    statusText?: string;
  }): void {
    if (!this.isReady()) return;
    try {
      posthog.capture('api_error', {
        http_status: params.status,
        api_url: params.url,
        http_method: params.method,
        error_message: params.message,
        status_text: params.statusText
      });
    } catch (err) {
      console.error('[AnalyticsService] captureApiError failed', err);
    }
  }

  /** Add or update super-properties applied to every subsequent event. */
  register(properties: Record<string, unknown>): void {
    if (!this.isReady()) return;
    try {
      posthog.register(properties);
    } catch (err) {
      console.error('[AnalyticsService] register failed', err);
    }
  }

  private isReady(): boolean {
    return this.isEnabled && this.isInitialized;
  }
}

/** Factory helper for use with `provideAppInitializer` or `APP_INITIALIZER`. */
export function initializeAnalyticsFactory(analyticsService: AnalyticsService): () => void {
  return () => analyticsService.init();
}

export type { AnalyticsConfig };
